<script setup lang="ts">
import {onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {CheckCircle, XCircle} from 'lucide-vue-next';
import {useGoogleCalendar} from '../composables/useGoogleCalendar';
import {refreshCalendarStatus} from '../composables/useHeaderState';

const route = useRoute();
const router = useRouter();
const {handleCallback} = useGoogleCalendar();

const isSuccess = ref<boolean | null>(null);
const isProcessing = ref(true);

onMounted(async () => {
  const code = route.query.code as string;
  const state = route.query.state as string;

  if (!code || !state) {
    isSuccess.value = false;
    isProcessing.value = false;
    return;
  }

  // Handle the callback
  const success = await handleCallback(code, state);
  isSuccess.value = success;
  isProcessing.value = false;

  // Redirect after 3 seconds if successful
  if (success) {
    await refreshCalendarStatus();
    setTimeout(() => {
      router.push('/onboarding');
    }, 3000);
  }
});
</script>

<template>
  <div class="flex min-h-screen bg-[#18181C] items-center justify-center p-6">
    <div
      class="w-full max-w-md bg-[#111113] rounded-3xl shadow-sm border border-[#2A2018] p-8 md:p-10"
    >
      <!-- Processing State -->
      <div v-if="isProcessing" class="text-center">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-[#1E1E23] rounded-3xl mb-6"
        >
          <div
            class="w-10 h-10 border-4 border-[#2A2018] border-t-[#F5F0EA] rounded-full animate-spin"
          ></div>
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('calendar_callback.processing_title') }}</h2>
        <p class="text-[#9E9080]">{{ $t('calendar_callback.processing_subtitle') }}</p>
      </div>

      <!-- Success State -->
      <div v-else-if="isSuccess === true" class="text-center">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-3xl mb-6"
        >
          <CheckCircle class="w-10 h-10 text-green-600"/>
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('calendar_callback.success_title') }}</h2>
        <p class="text-[#9E9080] mb-6">{{ $t('calendar_callback.success_message') }}</p>
        <p class="text-sm text-[#9E9080]">{{ $t('calendar_callback.success_redirecting') }}</p>
      </div>

      <!-- Error State -->
      <div v-else class="text-center">
        <div class="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-3xl mb-6">
          <XCircle class="w-10 h-10 text-red-600"/>
        </div>
        <h2 class="text-2xl text-[#F5F0EA] mb-2">{{ $t('calendar_callback.error_title') }}</h2>
        <p class="text-[#9E9080] mb-6">{{ $t('calendar_callback.error_message') }}</p>
        <button
          @click="router.push('/messenger')"
          class="px-6 py-3 bg-[#0A0A0B] text-[#F5F0EA] rounded-2xl hover:bg-[#111113] transition"
        >
          {{ $t('calendar_callback.back_to_app') }}
        </button>
      </div>
    </div>
  </div>
</template>
